import {observable, computed, runInAction, observer, React, _} from '@api'
import {RowNode, TabNode, TabSetNode, FlexLayoutNode, BorderNode, SplitterNode, IJsonTabNode} from '@flexlayout'
import {icons, globalStores, ui, ComponentSpecificTabMenuItems, SyncWorkspaceMenuItem} from '@stores'
import {Icon, MenuDivider, MenuItem} from '@blueprintjs/core'
import {DynamicFeed, Navigation} from '@material-ui/icons'
import {ChangeLogTabMenuItem, ContextMenuItem, ContextMenuItemProps, CurrentWorkspaceSyncToolbarButton, DefaultCatalogContextMenuItems, NewWhiteboardTabMenuItem, SaveCurrentCatalogAsPresetMenuItem, StoresInstanceContext, SwitchCurrentCatalogPresetMenuItem, useStores, useVipLocalStore, VipLocalStore, WorkspaceChooserAssortmentMenuItem, WorkspaceChooserAssortments, WorkspaceChooserMenuItems, WorkspacePresetMenuItems} from '@modules'
import {BuyingBoardUrl, CatalogRootFlexLayoutStore, FlexLayoutCurrentReportMenuItems, IC8TabConfig, IFlexLayoutChangeHistoryConfig, IFlexLayoutIFrameTabConfig, SyncWorkspaceTabMenuButton, TabGroupLabelMenuPanel, useCatalogRootFlexLayoutStore, useVipFlexLayoutTabStore, VipFlexLayoutTabStoreContext} from '@stores/catalog/flexLayout'
import {PrimaryIconButton} from 'centric-ui-components'
import {Fragment, MouseEventHandler} from 'react'
import * as css from './FlexLayoutContextMenu.css'

const SHOW_DEMO_FLEX_CREATION_ITEMS = false

interface FlexLayoutContextMenuExtra {
  splitter?: SplitterNode,
  tab?: TabNode,
  tabSet?: TabSetNode,
  border?: BorderNode
}

/// region Store

export class FlexLayoutContextMenuStore extends VipLocalStore {
  constructor(s) {
    super(s)
  }

  @observable mustHaveAtLeastOneObservable = true

  get extra(): FlexLayoutContextMenuExtra {
    return this.contextMenu.extra
  }

  get splitter(): SplitterNode {
    return this.extra?.splitter
  }

  get tab(): TabNode {
    return this.extra?.tab
  }

  get tabSet(): TabSetNode {
    return this.extra?.tabSet ?? this.extra?.tab?.tabSet
  }

  get border(): BorderNode {
    return this.extra?.border
  }

  @computed get tabIndex() {
    const {tabSet, tab} = this
    if (!tab || !tabSet) return -1

    const tabSetChildren = tabSet?.children ?? []

    return tabSetChildren.findIndex(n => n.id === tab.id)
  }

  @computed get component() {
    return this.tab?.attributes?.component
  }

  @computed get catalogRootTab(): Partial<CatalogRootFlexLayoutStore> {
    const r = this.stores.flexLayoutCatalogRoot

    if (!r) debugger

    return r ?? {viewMode: 'card'}
  }

  @computed get node() {
    return this.tab ?? this.splitter ?? this.border ?? this.tabSet
  }

  @computed get layoutContextMenu() { return this.node?.parent?.children[0] }

  @computed get viewMode() { return this.tab?.attributes?.config?.viewMode }

  @computed get isCardView() { return this.viewMode == 'card'}

  @computed get isTableView() { return this.viewMode == 'table'}

  @computed get isPdfView() { return this.viewMode == 'pdf'}
}

export const FlexLayoutContextMenuStoreContext = React.createContext<FlexLayoutContextMenuStore>(null)
export const useFlexLayoutContextMenuStore = () => React.useContext(FlexLayoutContextMenuStoreContext)

/// endregion

/// region Components

export const FlexLayoutContextMenu = observer(function FlexLayoutContextMenu() {
  const store = useVipLocalStore((s) => new FlexLayoutContextMenuStore(s)),
    {node, tab} = store

  const showLayoutMenuTopLevel = node instanceof SplitterNode || store.contextMenu.target.closest('[data-type="TabSet"]') != null,
    layoutMenuItems = <Fragment>
      {tab?.isGroupLabel
       ? <TabGroupLabelMenuPanel/>
       : tab ? <VipFlexLayoutTabStoreContext.Provider value={tab.vipTabStore}>
          <CatalogTabMenuItems/>
             </VipFlexLayoutTabStoreContext.Provider>
             : <Fragment>
         <FlexNodeMenuItems node={node}/>
       </Fragment>}
    </Fragment>

  return (
    <FlexLayoutContextMenuStoreContext.Provider value={store}>
      <VipFlexLayoutTabStoreContext.Provider value={tab?.vipTabStore}>
        {showLayoutMenuTopLevel ? layoutMenuItems
                                : <Fragment>
           <ContextMenuItem icon={icons.flexLayout} text='Report Layout'>
             {layoutMenuItems}
           </ContextMenuItem>
           <DefaultCatalogContextMenuItems/>
         </Fragment>}
      </VipFlexLayoutTabStoreContext.Provider>
    </FlexLayoutContextMenuStoreContext.Provider>
  )
})

/**
 * Right click on a flex layout tab
 */
export const CatalogTabMenuItems = observer(function CatalogTabMenuItems() {
  const tabMenuStore = useVipFlexLayoutTabStore()
  if (!tabMenuStore) return <NewFlexTab tabSet={globalStores.flexLayout.model?.activeTabset} text={'New Tab'}/>

  const {flexLayout: {isMaximized}, flexLayout, tab, tabSet, contextMenu} = tabMenuStore,
    stores = useStores(), {deployments: {isNewSync}} = stores

  // console.log({tab, tabSet})
  if (!tab && !tabSet) {
    //console.log(`No catalog tab menu items`, {component: tab?.attributes?.component})
    return null
  }

  if (tab?.isGroupLabel) {
    return <TabGroupLabelMenuPanel/>
  }

  const defaultItems = <Fragment>
    {(contextMenu?.store === tabMenuStore || contextMenu?.store === flexLayout ? null : contextMenu?.store?.contextMenuItems)}
    <DefaultCatalogContextMenuItems/>
  </Fragment>

  return (
    <StoresInstanceContext.Provider value={tabMenuStore.storesToInject}>
      {tab?.tabMenuItems}
      <SwitchFlexCatalogRootViewModeMenuItem catalogRootTab={tabMenuStore.tab.catalogRoot}/>
      {/*<WorkspaceChooserAssortmentsMenuItem catalogRootTab={tabMenuStore.tab.catalogRoot}/>*/}
      <SwitchPresetCurrentWorkspaceMenuItem catalogRootTab={tabMenuStore.tab.catalogRoot}/>

      <MenuDivider />

      <DuplicateTab />
      <MaximizeTab />
      <ContextMenuItem icon='page-layout' text='Tab Management'>
        {!flexLayout.isMaximized && <NewFlexTab tab={tab} tabSet={tabSet} text={'New Tab'}/>}
        <MenuDivider/>
        <RenameTab/>
        <MaximizeTab/>

        <MenuDivider/>

        <RearrangeTabsMenuItems/>
        <ContextMenuItem icon='duplicate' text='Duplicate...'>
          <DuplicateTab/>
          <DuplicateRight/>
          <DuplicateDown/>
        </ContextMenuItem>

        <CloseTabMenuItems/>

        <MenuDivider/>
        <ContextMenuItem text='Go to...' icon={<Navigation/>} popoverProps={{className: css.navigatorSubmenu, ...MenuItem.defaultProps.popoverProps}}>
          <FlexLayoutCurrentReportMenuItems/>
        </ContextMenuItem>

        {!isMaximized && <Fragment>
	        <MenuDivider/>
            <ToggleShowFlexTabHeaders/>
	        <ToggleShowFilterToolbarMenuItem/>
        </Fragment>}

      </ContextMenuItem>
      <MenuDivider/>

      <ComponentSpecificTabMenuItems tab={tabMenuStore.tab}/>

      {ui.settings.flexLayout.tabGroups && <GroupTabMenuItems/>}
      <MenuDivider/>
      {/*<MenuDivider title={`${name} Tab`}/>*/}

      {false && tab.component === 'catalog' &&
	   <Fragment>
		   <TabMenuPresetsMenuItem/>
		   <MenuDivider/>
	   </Fragment>}


      {/*<SwitchFlexTabWorkspacesMenuItem showCurrentWorkspace/>*/}

      {defaultItems}


      <MenuDivider/>

      {/*{flexLayout.isMaximized*/}
      {/* ? defaultItems*/}
      {/* : <Fragment>*/}
      {/*   <ContextMenuItem icon={icons.catalog} text='Catalog Actions'>*/}
      {/*     {defaultItems}*/}
      {/*   </ContextMenuItem>*/}
      {/* </Fragment>}*/}

      {/*<ContextMenuItem icon='prescription' text='Presets'>*/}
      {/*  <FlexLayoutPresetMenuItems/>*/}
      {/*</ContextMenuItem>*/}

      {/*/!*<MenuDivider title='Docking'/>*!/*/}
      {/*<ContextMenuItem icon='add-column-left' text='Docking'>*/}
      {/*  <FlexLayoutDockMenuItems/>*/}
      {/*</ContextMenuItem>*/}

      {/*<DevCatalogContextMenuItems/>*/}

     {!isNewSync && <SyncWorkspaceMenuItem dividerBefore />}

    </StoresInstanceContext.Provider>)
})

/**
 *
 */
export const TabMenuPresetsMenuItem = observer(function TabMenuPresetsMenuItems() {
  const catalogTabStore = useVipFlexLayoutTabStore()
  if (catalogTabStore?.tab?.component !== 'catalog' || !catalogTabStore?.tab?.catalogRoot) return null

  const {tab: {catalogRoot: {onSwitchTabWorkspace}}, storesToInject: {workspace, presets: {deletePreset, selectedPreset: preset}, currentUser}} = catalogTabStore

  if (!preset) {
    return (
      <Fragment>
        <SaveCurrentCatalogAsPresetMenuItem/>
        {/*<SwitchCurrentCatalogPresetMenuItem/>*/}
      </Fragment>
    )
  } else {
    return (
      <Fragment>
        <ContextMenuItem icon={preset.icon ?? icons.presets} text={preset.label}>
          {preset && preset.creator === currentUser.info.id && <ContextMenuItem dividerAfter text='Delete this Preset' icon='trash' onClick={() => deletePreset(preset)} shouldDismissPopover={false}/>}

          <WorkspacePresetMenuItems workspace={workspace?.config} onClickWorkspaceItem={onSwitchTabWorkspace}/>
        </ContextMenuItem>
        <SwitchCurrentCatalogPresetMenuItem/>
      </Fragment>
    )

    // return (<React.Fragment>
    //   <SaveCurrentCatalogAsPresetMenuItem/>
    //   <SwitchCurrentCatalogPresetMenuItem/>
    // </React.Fragment>)
  }
})

const RearrangeTabsMenuItems = observer(function RearrangeTabsMenuItems() {
  const {tab, flexLayout} = useVipFlexLayoutTabStore()

  return (<ContextMenuItem icon={icons.flexLayout} text='Rearrange...'>
    {/*<DuplicateTab/>*/}
    {/*<DuplicateRight/>*/}
    {/*<MoveTabHorizontal/>*/}
    {/*<DuplicateDown/>*/}
    {/*<MoveTabDynamic/>*/}

    {!flexLayout.isMaximized && <Fragment>
		<MoveTabHorizontal/>
		<MoveTabVertical/>
	</Fragment>}

    <MenuDivider/>

    <PinTab/>
    <MenuDivider/>
    <CombineTabsIntoSingleTabSet/>
    <TileCatalogTabsMenuItem/>

    <MenuDivider/>

    <SplitRight/>
    <SplitDown/>
    <Unsplit/>

    <MenuDivider/>

    <MoveToOppositeGroupMenuItem/>
    <ChangeSplitterOrientation/>

    {/*<MenuDivider/>*/}
    {/*<MoveTabToNewWindow/>*/}

  </ContextMenuItem>)
})

const CloseTabMenuItems = observer(function CloseTabMenuItems() {
  const {tab} = useVipFlexLayoutTabStore()

  return (
    <ContextMenuItem icon='trash' text='Close Tabs...'>
      <CloseThisTabMenuItem/>
      <CloseOtherTabsMenuItem/>
      <CloseAllTabsInGroup/>
      <CloseTabsToTheLeftMenuItem/>
      <CloseTabsToTheRightMenuItem/>
    </ContextMenuItem>)
})

/**
 * Right click on a specific tab/tabset/splitter
 */
const FlexNodeMenuItems = observer<{ node: FlexLayoutNode }>(function FlexNodeMenuItems({node}) {
  const store = useCatalogRootFlexLayoutStore()

  return node instanceof TabNode ? (
    <Fragment>
      <NewFlexTab tab={node} tabSet={node.tabSet}/>
      <DuplicateTab/>
      <CollectAllTabsOfSameType/>
      {store && <Fragment>
        {/*<SwitchFlexCatalogRootViewModeMenuItem catalogRootTab={store}/>*/}
        {/*<SwitchFlexTabWorkspacesMenuItem showCurrentWorkspace/>*/}
	  </Fragment>}
      <MenuDivider/>
    </Fragment>
  ) : node instanceof TabSetNode ? (
    <Fragment>
      <NewFlexTab tabSet={node}/>
      <MenuDivider/>
      <TabSetMenuItems/>
      <ToggleShowFlexTabHeaders/>
      <ToggleShowFilterToolbarMenuItem/>
    </Fragment>
  ) : node instanceof SplitterNode ? <SplitterMenuItems node={node}/> : null
})

const CollectAllTabsOfSameType = observer(function CollectAllTabsOfSameType() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {tab, flexLayout: {model, layout}} = store
  if (!tab) return null

  const tabsOfSameType = _.values(model._idMap).filter(v => v instanceof TabNode && v.component === tab.component && v != tab)

  return tabsOfSameType.length > 0 && <ContextMenuItem icon='collapse-all' text={`Collect Tabs (${tabsOfSameType.length + 1})`} onClick={tab.collectAllOfSameType}/>
})

const SplitterMenuItems = observer<{ node: SplitterNode }>(function SplitterMenuItems({node}) {
  const row = node.parent as RowNode,
    isColumn = row.orientation.name === 'vert',
    cellTerm = isColumn ? 'Rows' : 'Columns'

  return (<Fragment>
    <ContextMenuItem icon='split-columns' text={`Divide ${cellTerm} Equally`} onClick={() => node.onDoubleClick()}/>
    <ChangeSplitterOrientation/>
    <MenuDivider/>
    <ContextMenuItem icon='double-chevron-up' text={`Collapse ${isColumn ? 'Up' : 'Left'}`} onClick={() => node.onCollapseLeftOrUp()}/>
    <ContextMenuItem icon='double-chevron-down' text={`Expand ${isColumn ? 'Down' : 'Right'}`} onClick={() => node.onExpandRightOrDown()}/>
    <MenuDivider/>
    <Unsplit/>
  </Fragment>)
})
const ToggleShowFlexTabHeaders = observer(function ToggleShowFlexTabHeaders() {
  const store = useFlexLayoutContextMenuStore()
  if (!store) return null

  const {tabSet} = store
  if (!tabSet) return null

  return tabSet ? <ContextMenuItem shouldDismissPopover={false}
                                   active={tabSet.enableTabStrip !== false}
                                   text='Show Tabs'
                                   onClick={() => {
                                     runInAction(() => {
                                       tabSet.enableTabStrip = tabSet.enableTabStrip === false
                                       //flexLayout.modelJson['__id'] = guid() // Force a layout pass
                                     })
                                   }}/> : null
})

export const SwitchFlexTabWorkspacesMenuItem = observer<{ showCurrentWorkspace?: boolean }>(function SwitchFlexTabWorkspacesMenuItem({showCurrentWorkspace}) {
  const store = useCatalogRootFlexLayoutStore()

  if (!store) return null

  const {tab, onSwitchTabWorkspace} = store
  return tab ? (
    <ContextMenuItem icon='applications' text={showCurrentWorkspace ? `Change Tab View to...` : `Switch to Workspace...`}>
      <WorkspaceChooserMenuItems onClickWorkspaceItem={onSwitchTabWorkspace}/>
    </ContextMenuItem>
  ) : null
})

const TabSetMenuItems = observer(function TabSetMenuItems() {
  return (<Fragment>
    <ChangeSplitterOrientation/>
    <MaximizeTab/>
    <CloseAllTabsInGroup/>
  </Fragment>)
})

const MaximizeTab = observer(function MaximizeTab() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {toggleMaximize, settings: {maximizedTab}}, tabSet, tab} = store

  const isMaximized = tab.id === maximizedTab

  return !isMaximized && tabSet ? (<ContextMenuItem shouldDismissPopover icon={isMaximized ? 'minimize' : 'maximize'} text={isMaximized ? 'Restore Tabs' : `Maximize Tab`} onClick={() => toggleMaximize(tab ?? tabSet.selectedNode as TabNode)}/>) : null
})

const NewFlexTab = observer<{ text?: string, tab?: TabNode, tabSet: TabSetNode }>(function NewFlexTab({text, tab, tabSet}) {
  return tabSet?.json ? (<ContextMenuItem shouldDismissPopover icon='new-layer' text={text ?? (!tab ? 'New Tab in Group' : 'New Tab')}>
      <NewFlexTabMenuItems tab={tab} tabSet={tabSet}/>
    </ContextMenuItem>
  ) : null
})

export const NewFlexTabMenuItems = observer<{ tab?: TabNode, tabSet?: TabSetNode }>(function NewFlexTabMenuItems({tab, tabSet}) {
  return <Fragment>
    {tab && <Fragment>
		<DuplicateTab/>
    <NewWhiteboardTabMenuItem tab={tab} tabSet={tabSet}/>
		<MenuDivider/>
	</Fragment>}
    <FlexTabComponentMenuItems action='new' tab={tab} tabSet={tabSet ?? tab?.tabSet}/>
  </Fragment>
})

const FlexTabComponentMenuItems = observer<{ action?: 'new' | 'update', tab?: TabNode, tabSet?: TabSetNode }>(function FlexTabComponentMenuItems(props) {
  const store = useStores(),
    {tab, tabSet} = props

  if (!store) return null

  const action = props.action ?? 'new',
    {flexLayout: {create: {onNewOrUpdateTabFromWorkspaceItem}}} = store,
    catalogRoot = tab?.catalogRoot

  return <Fragment>
  {/*  {false && catalogRoot && <Fragment>*/}
	{/*	<ContextMenuItem*/}
	{/*		shouldDismissPopover*/}
	{/*		text='Card View'*/}
	{/*		active={catalogRoot?.catalog?.settings?.viewMode === 'card'}*/}
	{/*		labelElement={<Icon icon={icons.card}/>}*/}
	{/*		onClick={() => createOrUpdateTabView({*/}
  {/*            action, tab, tabSet,*/}
  {/*            tabJson: cardTab*/}
  {/*          })}/>*/}
	{/*	<ContextMenuItem*/}
	{/*		shouldDismissPopover*/}
	{/*		text='Table View'*/}
	{/*		active={catalogRoot?.catalog?.settings?.viewMode === 'table'}*/}
	{/*		labelElement={<Icon icon={icons.table}/>}*/}
	{/*		onClick={() => createOrUpdateTabView({*/}
  {/*            action, tab, tabSet,*/}
  {/*            tabJson: tableTab*/}
  {/*          })}/>*/}
	{/*	<ContextMenuItem*/}
	{/*		shouldDismissPopover*/}
	{/*		text='Pdf View'*/}
	{/*		active={catalogRoot?.catalog?.settings?.viewMode === 'pdf'}*/}
	{/*		labelElement={<Icon icon={icons.pdf}/>}*/}
	{/*		onClick={() => createOrUpdateTabView({*/}
  {/*            action, tab, tabSet,*/}
  {/*            tabJson: pdfTab*/}
  {/*          })}/>*/}
	{/*</Fragment>}*/}

    {/*<MenuDivider title='Workspaces'/>*/}
    <WorkspaceChooserMenuItems
      shouldDismissPopover //={action === 'new'} // otherwise the menu won't always disappear correctly as the button gets reloaded
      onClickWorkspaceItem={(e, v) => onNewOrUpdateTabFromWorkspaceItem(e, {...v, tabSet, tab: action === 'update' ? tab : undefined})}
      showSelected={catalogRoot != null}
      hideSaveAsPreset={action === 'update'}
    />

    <MenuDivider/>

    <ChangeLogTabMenuItem tab={tab} tabSet={tabSet} action='new'/>
    <NewWhiteboardTabMenuItem tab={tab} tabSet={tabSet} action='new'/>
    <NewPdfTabMenuItem tab={tab} tabSet={tabSet} action='new'/>
    <OtherNewFlexTabMenuItems {...props} />
  </Fragment>
})

export type NewFlexLayoutTabMenuItemProps = { action?: 'new' | 'update', tab?: TabNode, tabSet?: TabSetNode } & ContextMenuItemProps

export const NewPdfTabMenuItem = observer<NewFlexLayoutTabMenuItemProps>(function NewWhiteboardTabMenuItem({className, action, tab, tabSet, ...rest}) {
  const {catalog: {isServerPDFAvailable}, flexLayout: {create: {createPdfTab}}} = useStores()

  if (!isServerPDFAvailable) return null

  return (
    <ContextMenuItem
      {...rest}
      shouldDismissPopover
      icon={icons.pdf}
      text={`PDF Viewer`}
      //active={tab?.component === 'vipBuying' && ((tab.config as IFlexLayoutIFrameTabConfig)?.settings?.url === BuyingBoardUrl.retail)}
      onClick={() => {
        createPdfTab({tab, tabSet})
      }}
    />)
})

export const OtherNewFlexTabMenuItems = observer<{ action?: 'new' | 'update', tab?: TabNode, tabSet?: TabSetNode }>(function OtherFlexTabMenuItems(props) {
  const store = useStores(),
    {tab, tabSet} = props

  if (!store) return null

  const action = props.action ?? 'new',
    {flexLayout: {create: {createOrUpdateTabView, onNewOrUpdateTabFromWorkspaceItem}, catalogTabs, presetsStore: {googleSearchTab, c8Tab, vipBuyingBoardTab}}, deployments: {deploymentConfig: {availableIFrames}}} = store,
    existingCount = catalogTabs?.length ?? 0,
    suffix = existingCount ? ` (${existingCount})` : '',
    extraFrames = _.groupBy(availableIFrames ?? [], f => f.category)

  return (<Fragment>
    {_.keys(extraFrames).map((k, i) => <Fragment key={i}>
      <MenuDivider title={k}/>
      {extraFrames[k].map(({label, url, injectCss, icon}, j) => (<ContextMenuItem
        key={j}
        shouldDismissPopover
        icon={'blank'}
        text={label}
        onClick={() => createOrUpdateTabView({
          action, tab, tabSet, tabJson: {
            type: 'tab',
            component: 'iframe',
            name: label,
            config: {settings: {url, injectCss, icon}}
          }
        })}/>))}
    </Fragment>)}

    {DEBUG && SHOW_DEMO_FLEX_CREATION_ITEMS && <Fragment>
		<MenuDivider title='VIP'/>
		<ContextMenuItem
			shouldDismissPopover
			icon={'blank'}
			text='Buying Board (Retail)'
			active={tab?.component === 'vipBuying' && ((tab.config as IFlexLayoutIFrameTabConfig)?.settings?.url === BuyingBoardUrl.retail)}
			onClick={() => createOrUpdateTabView({
              action, tab, tabSet, tabJson: {...vipBuyingBoardTab, name: 'Retail', config: {settings: {url: BuyingBoardUrl.retail}}}
            })}/>
		<ContextMenuItem
			shouldDismissPopover
			icon={'blank'}
			text='Buying Board (Wholesale)'
			active={tab?.component === 'vipBuying' && ((tab.config as IFlexLayoutIFrameTabConfig)?.settings?.url === BuyingBoardUrl.wholesale)}
			onClick={() => createOrUpdateTabView({
              action, tab, tabSet, tabJson: {...vipBuyingBoardTab, name: 'Wholesale', config: {settings: {url: BuyingBoardUrl.wholesale}}}
            })}/>
		<ContextMenuItem
			shouldDismissPopover
			icon={'blank'}
			text='Buying Board (Localhost)'
			active={tab?.component === 'vipBuying' && ((tab.config as IFlexLayoutIFrameTabConfig)?.settings?.url === BuyingBoardUrl.localhost)}
			onClick={() => createOrUpdateTabView({
              action, tab, tabSet, tabJson: {...vipBuyingBoardTab, name: 'Wholesale', config: {settings: {url: BuyingBoardUrl.localhost}}}
            })}/>
		<MenuDivider title='C8'/>
		<ContextMenuItem
			shouldDismissPopover
			icon={'blank'}
			text='C8'
			onClick={() => createOrUpdateTabView({
              action, tab, tabSet,
              tabJson: c8Tab
            })}/>
		<ContextMenuItem
			shouldDismissPopover
			icon={'blank'}
			text='Collection Targets'
			onClick={() => createOrUpdateTabView({
              action, tab, tabSet,
              tabJson: {
                ...c8Tab,
                name: 'Collection Targets',
                config: {
                  settings: {
                    queryString: '#URL=C387650&Tab=Products&NR=1'
                  }
                } as Partial<IC8TabConfig>
              }
            })}/>
		<MenuDivider title='Other'/>
		<ContextMenuItem
			shouldDismissPopover
			active={tab?.component === 'iframe' && _.isEqual((tab.config as IFlexLayoutIFrameTabConfig)?.settings?.url, googleSearchTab.config.url)}
			text='Google Search'
			onClick={() => createOrUpdateTabView({
              action, tab, tabSet,
              tabJson: googleSearchTab
            })}/>
	</Fragment>}
  </Fragment>)
})

const GroupTabMenuItems = observer(function AddTabToGroupMenu() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {tab, tabSet, flexLayout: {tabGroups}} = store

  if (!tab) return null

  if (tab.isGroupLabel) {
    return <Fragment>
      <div>foo</div>
    </Fragment>
  } else {
    const {tabGroup} = tab
    if (tabGroup) {
      return <Fragment>
        <AddTabToGroup/>
        <RemoveTabFromGroup/>
      </Fragment>
    }
    return (
      <Fragment>
        <AddTabToGroup/>
      </Fragment>)
  }
})
const RemoveTabFromGroup = observer(function RemoveTabFromGroup() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {tab, tabSet, flexLayout: {tabGroups}} = store

  if (!tab) return null

  return <ContextMenuItem icon='blank' text='Remove Tab from Group' onClick={e => tab.removeFromParent()}/>
})

const AddTabToGroup = observer(function AddTabToGroup() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {tab, tab: {canAddTabToGroup}, flexLayout: {tabGroups, addTabToNewGroup}} = store

  if (!tab) return null
  return (<Fragment>
    <ContextMenuItem shouldDismissPopover disabled={!canAddTabToGroup} icon='blank' text={'Add Tab to Group'}>
      <ContextMenuItem text='New Group' onClick={() => addTabToNewGroup(tab)}/>
      {!_.isEmpty(tabGroups) && <Fragment>
		  <MenuDivider/>
        {_.values(tabGroups).map((tg, i) => <ContextMenuItem key={i} icon={<span style={{backgroundColor: tg.groupColor}} className={css.groupColor}/>} text={tg.name} onClick={() => tab.setTabGroup(tg)}/>)}
	  </Fragment>}
    </ContextMenuItem>
  </Fragment>)
})
/// regionTab Management (Splits)

const CombineTabsIntoSingleTabSet = observer(function CombineCatalogTabsIntoSingleGroupMenuItem() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {
      flexLayout: {
        //activeCatalogTabs,
        combineAllTabGroups, model,
        tabSets, onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction
      }, tabSet,
      contextMenu
    } = store,
    disabled = !tabSets || tabSets.length <= 1

  return disabled ? null : <ContextMenuItem
    labelElement={<PreviewLayoutButton
      disabled={disabled}
      onMouseEnter={e => onStartPreviewLayoutInteraction(model.root, t => combineAllTabGroups(true))}
      onMouseLeave={e => onEndPreviewLayoutInteraction(tabSet)}/>}
    icon='merge-columns' text={`Combine Tab Groups`} onClick={() => combineAllTabGroups()}/>
})

const TileCatalogTabsMenuItem = observer(function TileCatalogTabsMenuItem() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {
      flexLayout: {
        tileWindows, model,
        onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction
      }, tabSet
    } = store,
    allTabs = model?.nodesByType?.tab ?? []

  return allTabs?.length > 1 && <ContextMenuItem icon='grid-view' text={`Tile Windows`}
                                                 labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(model.root, t => tileWindows(true))}
                                                                                    onMouseLeave={e => onEndPreviewLayoutInteraction(tabSet)}/>}
                                                 onClick={() => tileWindows()}/>
})

const PreviewLayoutButton = observer<{ disabled?: boolean, onMouseEnter?: MouseEventHandler<HTMLDivElement>, onMouseLeave?: MouseEventHandler<HTMLDivElement> }>(function PreviewLayoutButton({disabled, onMouseEnter, onMouseLeave}) {
  return <PrimaryIconButton tooltip='Preview Layout Change' disabled={disabled} wrapperClassName={css.previewButton} icon='lightbulb' onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}/>
})

const Unsplit = observer(function Unsplit() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, unsplit}, node, splitter, tab, tabSet} = store

  if (!(tab || tabSet || splitter)) return null

  const tabSetParent = tabSet?.parent || splitter?.parent,
    disabled = tabSetParent && tabSetParent.nonSidebarChildren.length <= 1

  return disabled ? null : (tab || splitter) && <ContextMenuItem
	  text={node instanceof SplitterNode ? 'Remove Split' : 'Unsplit'}
	  icon='remove-column'
	  disabled={disabled}
	  labelElement={<PreviewLayoutButton disabled={disabled} onMouseEnter={e => onStartPreviewLayoutInteraction(node, (t: TabNode | SplitterNode) => unsplit(t))}
                                         onMouseLeave={e => onEndPreviewLayoutInteraction(node)}/>}
	  onClick={() => unsplit(node)}/>
})

const SplitRight = observer(function SplitRight() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {moveTabs, onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction}, tab, tabsToMyLeft, tabsToMyRight} = store,
    disabled = _.isEmpty(tabsToMyRight) && _.isEmpty(tabsToMyLeft)

  if (!tab || disabled) return null

  return <ContextMenuItem
    disabled={disabled}
    labelElement={<PreviewLayoutButton
      disabled={disabled}
      onMouseEnter={e => onStartPreviewLayoutInteraction(tabsToMyRight, t => moveTabs(t as Array<TabNode>, 'right'))}
      onMouseLeave={e => onEndPreviewLayoutInteraction(tabsToMyRight)}/>}
    icon='split-columns' text={`Split Right`} onClick={() => moveTabs(tabsToMyRight, 'right')}/>
})

const SplitDown = observer(function SplitDown() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {moveTabs, onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction}, tab, tabsToMyLeft, tabsToMyRight} = store,
    disabled = _.isEmpty(tabsToMyRight) && _.isEmpty(tabsToMyLeft)

  if (!tab || disabled) return null
  return <ContextMenuItem
    disabled={disabled}
    labelElement={<PreviewLayoutButton
      disabled={disabled}
      onMouseEnter={e => onStartPreviewLayoutInteraction(tabsToMyRight, t => moveTabs(t as Array<TabNode>, 'down'))}
      onMouseLeave={e => onEndPreviewLayoutInteraction(tabsToMyRight)}/>}
    icon='arrows-vertical' text={`Split Down`} onClick={() => moveTabs(tabsToMyRight, 'down')}/>
})

const DuplicateTab = observer(function DuplicateTab() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction}, tab} = store
  return tab && (
    <ContextMenuItem
      labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, (t: TabNode) => t.duplicate('inline'))}
                                         onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
      icon='duplicate' text={`Duplicate this Tab`} onClick={() => tab.duplicate('inline')}/>
  )
})

const DuplicateRight = observer(function SplitRight() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction}, tab} = store
  return tab && <ContextMenuItem
	  icon='arrow-right'
	  text='Duplicate Right'
	  labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, (t: TabNode) => t.duplicate('right'))}
                                         onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
	  onClick={() => tab.duplicate('right')}/>
})

const DuplicateDown = observer(function SplitDown() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction}, tab} = store
  return tab && <ContextMenuItem
	  icon='arrow-down'
	  text='Duplicate Down'
	  labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, (t: TabNode) => t.duplicate('down'))}
                                         onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
	  onClick={() => tab.duplicate('down')}/>
})

const MoveTabDynamic = observer(function MoveTabDynamic() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, moveTab}, tab, tabSet} = store

  if (!tabSet || !tab) return null

  const rowOrColumn = tabSet.parent as RowNode,
    grandParentRow = rowOrColumn.parent as RowNode

  let indexInParent = rowOrColumn.children.indexOf(tabSet)
  if (rowOrColumn.orientation.name === 'vert') {
    if (rowOrColumn.nonSidebarChildren.length === 1) {
      return null
    }
  }

  /**
   * 3 TabSets:
   * A | C
   * -----
   * B | C
   *
   * Right click on A should have 'Move to Bottom' and 'Move to Right'
   * Right click on B should have 'Move to Top' and 'Move to Right'
   * Right click on C should have 'Move to Top' and 'Move to Bottom'
   *
   */
  return <ContextMenuItem icon='blank'
                          text={`Move ${indexInParent == 0 ? 'Down' : 'Up'}`}
                          labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => moveTab(t as TabNode, indexInParent === 0 ? 'down' : 'up'))}
                                                             onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                          onClick={() => moveTab(tab, indexInParent === 0 ? 'down' : 'up')}/>
})

const MoveTabHorizontal = observer(function MoveTabHorizontal() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, moveTab}, tab, tabSet} = store

  if (!tab || !tabSet) return null

  const rowOrColumn = tabSet.parent as RowNode,
    grandParentRow = rowOrColumn?.parent as RowNode

  if (!rowOrColumn) debugger
  /**
   * Should not Show in following case:
   * A
   * B
   *
   * In above the layout will be
   * [{type: row, children: [  // Horizontal row
   *  {type: row, children: [ // vertical column
   *    { tabset}
   *    { tabset}
   *  ]}
   *
   */

    // console.log(toJS({rowOrColumn, grandParentRow, tabSet}))

  const {leftOrTop, rightOrBottom} = tabSet

  return <Fragment>
    <ContextMenuItem icon='arrow-left'
                     disabled={leftOrTop?.isLayoutSidebar}
                     text={`Move Left`}
                     labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => moveTab(t as TabNode, 'left'))}
                                                        onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                     onClick={() => moveTab(tab, 'left')}/>
    <ContextMenuItem icon='arrow-right'
                     text={`Move Right`}
                     labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => moveTab(t as TabNode, 'right'))}
                                                        onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                     onClick={() => moveTab(tab, 'right')}/>
  </Fragment>
})

const MoveTabVertical = observer(function MoveTabVertical() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, moveTab}, tab, tabSet} = store

  if (!tab || !tabSet) return null

  const rowOrColumn = tabSet.parent as RowNode,
    grandParentRow = rowOrColumn?.parent as RowNode

  if (!rowOrColumn) debugger
  /**
   * Should not Show in following case:
   * A
   * B
   *
   * In above the layout will be
   * [{type: row, children: [  // Horizontal row
   *  {type: row, children: [ // vertical column
   *    { tabset}
   *    { tabset}
   *  ]}
   *
   */

    // console.log(toJS({rowOrColumn, grandParentRow, tabSet}))

  const {leftOrTop, rightOrBottom} = tabSet
  return <Fragment>
    <ContextMenuItem icon='arrow-up'
                     text={`Move Up`}
                     labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => moveTab(t as TabNode, 'up'))}
                                                        onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                     onClick={() => moveTab(tab, 'up')}/>
    <ContextMenuItem icon='arrow-down'
                     text={`Move Down`}
                     labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => moveTab(t as TabNode, 'down'))}
                                                        onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                     onClick={() => moveTab(tab, 'down')}/>
  </Fragment>
})

/// endregion
const MoveToOppositeGroupMenuItem = observer(function MoveToOppositeGroupMenuItem() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, moveToOppositeGroup}, tab, tabSet} = store

  if (!tabSet) return null

  const tabSetParent = tabSet.parent

  return tabSetParent.nonSidebarChildren.length > 1 &&
         <ContextMenuItem icon='blank' text='Move to Opposite Group'
                          labelElement={<PreviewLayoutButton onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => moveToOppositeGroup(t as TabNode))}
                                                             onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                          onClick={() => moveToOppositeGroup(tab)}/>
})

const ChangeSplitterOrientation = observer(function ChangeSplitterOrientation() {
  const store = useFlexLayoutContextMenuStore()

  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, changeSplitterOrientation}, node, tabSet} = store

  if (node instanceof SplitterNode) {} else if (node instanceof TabNode) {
    if (!tabSet) return null

    const tabSetParent = tabSet.parent
    if (tabSetParent.nonSidebarChildren.length <= 1) return null
  } else {
    return null
  }

  return <ContextMenuItem icon='rotate-document' text={`Change Split Orientation`}
                          labelElement={<PreviewLayoutButton
                            onMouseEnter={e => onStartPreviewLayoutInteraction(node, (n: TabNode | SplitterNode) => changeSplitterOrientation(n))}
                            onMouseLeave={e => onEndPreviewLayoutInteraction(node)}/>}
                          onClick={() => changeSplitterOrientation(node)}/>
})

const PinTab = observer(function PinTab() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, togglePinTab}, tab, tabSet, config} = store
  if (!tabSet) return null

  return tab && <ContextMenuItem icon='pin' text={config?.pin === true ? 'Unpin Tab' : 'Pin Tab'}
                                 labelElement={<PreviewLayoutButton
                                   onMouseEnter={e => onStartPreviewLayoutInteraction(tab, t => togglePinTab(t as TabNode))}
                                   onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                                 onClick={() => togglePinTab(tab)}/>
})

const MoveTabToNewWindow = observer(function MoveTabToNewWindow() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {moveTabToNewWindow}, tab, tabSet} = store

  if (!tabSet) return null

  return tab && <ContextMenuItem disabled shouldDismissPopover icon='blank' text='Move to New Window' onClick={() => moveTabToNewWindow(tab)}/>
})

const RenameTab = observer(function RenameTab() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {onStartRenameTab}, tab} = store
  return tab && <ContextMenuItem shouldDismissPopover icon='edit' text='Rename Tab' onClick={() => onStartRenameTab(tab)}/>
})

// <ContextMenuItem shouldDismissPopover icon='arrow-right' text='Duplicate and Split Right' onClick={() => duplicateAndSplitTab(node, 'right')}/>
//

const CloseThisTabMenuItem = observer(function CloseThisTabMenuItem() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, removeNodeFromParent}, tab} = store
  return tab && <ContextMenuItem icon='blank' text={`Close Tab`} onClick={() => removeNodeFromParent(tab, true)}/>
})

const CloseOtherTabsMenuItem = observer(function CloseOtherTabsMenuItem() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, closeOtherTabsInGroup}, tab} = store

  if (!tab) return null

  const disabled = tab.parent.nonSidebarChildren.length <= 1

  return tab && <ContextMenuItem icon='blank'
                                 disabled={disabled} text={`Close Other Tabs`}
                                 labelElement={<PreviewLayoutButton
                                   disabled={disabled}
                                   onMouseEnter={e => onStartPreviewLayoutInteraction(tab, (t: TabNode) => closeOtherTabsInGroup(t))}
                                   onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                                 onClick={() => closeOtherTabsInGroup(tab)}/>
})

const CloseAllTabsInGroup = observer(function CloseAllTabsInGroup() {
  const store = useFlexLayoutContextMenuStore()

  if (!store) return null

  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, tabSets}, tab, tabSet} = store
  if (!tabSet) return null

  const disabled = tabSet.children.length == 0

  return <ContextMenuItem shouldDismissPopover
                          disabled={disabled} icon='blank' text={`Close Tabs in Group (${tabSet.children.length})`}
                          labelElement={<PreviewLayoutButton
                            disabled={disabled}
                            onMouseEnter={e => onStartPreviewLayoutInteraction(tabSet, (ts: TabSetNode) => ts?.closeAllTabsInGroup())}
                            onMouseLeave={e => onEndPreviewLayoutInteraction(tabSet)}/>}
                          onClick={() => tabSet.closeAllTabsInGroup(true)}/>
})

const CloseTabsToTheLeftMenuItem = observer(function CloseTabsToTheLeftMenuItem() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, closeTabsToTheLeft}, tab, tabSet} = store

  if (!tab) return null
  return tab.indexInParent > 0 &&
         <ContextMenuItem icon='blank' text='Close Tabs to the Left'
                          labelElement={<PreviewLayoutButton
                            onMouseEnter={e => onStartPreviewLayoutInteraction(tab, (t: TabNode) => closeTabsToTheLeft(t))}
                            onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                          onClick={() => closeTabsToTheLeft(tab)}/>
})

const CloseTabsToTheRightMenuItem = observer(function CloseTabsToTheRight() {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {flexLayout: {onStartPreviewLayoutInteraction, onEndPreviewLayoutInteraction, closeTabsToTheRight}, tab, tabSet} = store

  if (!tab || !tabSet) return null

  const {indexInParent} = tab

  return indexInParent != -1 && indexInParent + 1 < tabSet.children.length &&
         <ContextMenuItem icon='blank' text='Close Tabs to the Right'
                          labelElement={<PreviewLayoutButton
                            onMouseEnter={e => onStartPreviewLayoutInteraction(tab, (t: TabNode) => closeTabsToTheRight(t))}
                            onMouseLeave={e => onEndPreviewLayoutInteraction(tab)}/>}
                          onClick={() => closeTabsToTheRight(tab)}/>
})

const ToggleShowFilterToolbarMenuItem = observer<ContextMenuItemProps>(function ToggleShowFilterToolbarMenuItem(props) {
  const {filterChipsToolbar: {hasDataToShow}} = useStores(),
    {catalog: {settings: {toolbarItems}}} = globalStores

  if (!hasDataToShow || !toolbarItems) return null

  const {filterChips} = toolbarItems  
  return <ContextMenuItem {...props} text='Show Tab Toolbars' active={filterChips.visible} onClick={() => filterChips.visible = !filterChips.visible}/>
})

export const WorkspaceChooserAssortmentsMenuItem = observer<{ wrapWithMenuItem?: boolean, catalogRootTab: Partial<CatalogRootFlexLayoutStore> }>(function SwitchPresetCurrentWorkspaceMenuItem({wrapWithMenuItem, catalogRootTab}) {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {tab, strings, flexLayout: {create: {onNewOrUpdateTabFromWorkspaceItem}}} = store
  if (!tab?.storesToInject?.catalog?.canShowAssortment) return null

  const {storesToInject: {workspace, catalog}} = tab,
    r = <WorkspaceChooserAssortments
      workspace={workspace?.config}
      onClickWorkspaceItem={(e, v) => onNewOrUpdateTabFromWorkspaceItem(e, {
        ...v,
        tab,
        preset: v.preset ? (v.preset._id === catalog.settings.preset ? null : v.preset) : null,
        assortment: v.assortment ? (v.assortment._id === catalog.settings.assortment?.id ? null : v.assortment) : null
      })}
    />

  return !wrapWithMenuItem ? r : <ContextMenuItem icon={icons.heroCards} text={strings.assortments}>
    {r}
  </ContextMenuItem>
})

export const SwitchPresetCurrentWorkspaceMenuItem = observer<{ catalogRootTab: Partial<CatalogRootFlexLayoutStore> }>(function SwitchPresetCurrentWorkspaceMenuItem({catalogRootTab}) {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null
  const {tab, strings, flexLayout: {create: {onNewOrUpdateTabFromWorkspaceItem}}} = store
  if (tab?.component != 'catalog' || !tab.storesToInject) return null

  const {storesToInject: {catalog: {presets: {selectedPreset: preset, deletePreset}, stores: {currentUser}}, workspace}} = tab
  return <ContextMenuItem icon={icons.presets} text={'Workspace Presets...'}>
    <WorkspacePresetMenuItems
      shouldDismissPopover={true}
      workspace={workspace?.config}
      onClickWorkspaceItem={(e, v) => onNewOrUpdateTabFromWorkspaceItem(e, {...v, tab})}
    />
    {preset && <ContextMenuItem dividerBefore text='Delete this Preset' icon='trash' disabled={preset.creator !== currentUser?.info?.id} onClick={() => deletePreset(preset)} shouldDismissPopover={false}/>}
  </ContextMenuItem>
})

export const SwitchFlexCatalogRootViewModeMenuItem = observer<{ catalogRootTab: Partial<CatalogRootFlexLayoutStore> }>(function SwitchFlexCatalogRootViewModeMenuItem({catalogRootTab}) {
  const store = useVipFlexLayoutTabStore()
  if (!store) return null

  const {tab, strings} = store
  if (!tab) return null

  return <ContextMenuItem icon={<DynamicFeed/>} text={strings.goToWorkspace}>
    <FlexTabComponentMenuItems action='update' tab={tab} tabSet={tab.tabSet}/>
  </ContextMenuItem>

  // if (!catalogRootTab) {
  //
  // }
  //
  // const {viewMode, icon} = catalogRootTab
  // return (
  //   <ContextMenuItem icon={icon ?? 'blank'} text={`Switch '${viewMode === 'card' ? strings.cardView : viewMode === 'table' ? strings.tableView : viewMode === 'pdf' ? strings.pdfView : 'Unknown'}' to...`}>
  //     {/*<SwitchFlexCatalogRootViewModeMenuItems catalogRootTab={catalogRootTab}/>*/}
  //     <FlexTabComponentMenuItems action='update'/>
  //   </ContextMenuItem>)
})

export const SwitchFlexCatalogRootViewModeMenuItems = observer<{ catalogRootTab: Partial<CatalogRootFlexLayoutStore> }>(function SwitchFlexCatalogRootViewModeMenuItem({catalogRootTab}) {
  if (!catalogRootTab) return null

  const {isCardView, isPdfView, isTableView, catalog: {strings, settings, isActionButtonEnabled}, stores: {config: {deployment: {views}}}} = catalogRootTab

  return (
    <Fragment>
      {/* Make sure to dismiss the context menu or else our extra data will be invalid after this update as the node will change */}
      <ContextMenuItem active={isCardView} icon={isCardView ? 'tick' : 'blank'} text={strings.cardView} onClick={() => settings.viewMode = 'card'} rightIcon={icons.card}/>
      {isActionButtonEnabled('tableView') && <ContextMenuItem active={isTableView} rightIcon={icons.table} text={strings.tableView} onClick={() => settings.viewMode = 'table'}/>}
      <ContextMenuItem active={isPdfView} rightIcon={icons.pdf} text={strings.pdfView} onClick={() => settings.viewMode = 'pdf'}/>
    </Fragment>)
})



// WEBPACK FOOTER //
// ./src/modules/contextMenus/menus/FlexLayoutContextMenu.tsx